/**
 * AssetQuestionnaireStep - Step 2 of Site Calibration Wizard
 *
 * User describes site assets (Solar, BESS, EV Fleet, Cold Storage)
 * and selects a tariff. Calls PhD API to build digital twin.
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
  Collapse,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  WbSunny as SolarIcon,
  Battery5Bar as BatteryIcon,
  ElectricCar as EvIcon,
  AcUnit as ColdIcon,
} from '@mui/icons-material';
import { vortxColors } from '@vortx/shared-ui';

const PHD_API_BASE = process.env.REACT_APP_PHD_API_URL || 'http://localhost:5002';

const RELATIONSHIP_MAP = {
  'N/A': 'not_applicable',
  'Existing': 'already_in_load',
  'New': 'adding_new',
  'Both': 'both',
};

const RELATIONSHIP_OPTIONS = ['N/A', 'Existing', 'New', 'Both'];

const UTILITY_RATES = {
  PGE:   { label: 'PG&E (E-19)', onPeak: 0.15, midPeak: 0.10, offPeak: 0.07, dc: 20, peakDc: 5 },
  SCE:   { label: 'SCE (TOU-GS-3)', onPeak: 0.14, midPeak: 0.09, offPeak: 0.06, dc: 22, peakDc: 6 },
  SDGE:  { label: 'SDG&E (AL-TOU)', onPeak: 0.16, midPeak: 0.11, offPeak: 0.08, dc: 18, peakDc: 4 },
  ComEd: { label: 'ComEd (Flat)', onPeak: 0.08, midPeak: 0.08, offPeak: 0.08, dc: 12, peakDc: 0 },
  Custom: { label: 'Custom Rate' },
};
const UTILITIES = Object.keys(UTILITY_RATES);

const INITIAL_ASSETS = {
  solar: { relationship: 'N/A', capacity_kw: '' },
  battery: { relationship: 'N/A', capacity_kwh: '', power_kw: '' },
  ev: { relationship: 'N/A', num_vehicles: '', charger_type: 'DCFC', arrival_hour: '', departure_hour: '' },
  cold_storage: { relationship: 'N/A', sqft: '', setpoint_f: '' },
};

const cardSx = {
  border: `1px solid ${vortxColors.teal}33`,
  '&:hover': { borderColor: vortxColors.gold },
};

const iconSx = { color: vortxColors.gold, mr: 1 };

export default function AssetQuestionnaireStep({ load_8760_kw, location, onComplete, onBack }) {
  const [assets, setAssets] = useState(INITIAL_ASSETS);
  const [tariffType, setTariffType] = useState('PGE');
  const [customTariff, setCustomTariff] = useState({ energy_rate: '', demand_charge: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const updateAsset = (key, field, value) => {
    setAssets(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const buildApiPayload = () => {
    const apiAssets = {};
    for (const [key, asset] of Object.entries(assets)) {
      const relationship = RELATIONSHIP_MAP[asset.relationship];
      const entry = { relationship };
      if (relationship !== 'not_applicable') {
        if (key === 'solar' && asset.capacity_kw) entry.capacity_kw = Number(asset.capacity_kw);
        if (key === 'battery') {
          if (asset.capacity_kwh) entry.capacity_kwh = Number(asset.capacity_kwh);
          if (asset.power_kw) entry.power_kw = Number(asset.power_kw);
        }
        if (key === 'ev') {
          if (asset.num_vehicles) entry.num_vehicles = Number(asset.num_vehicles);
          if (asset.charger_type) entry.charger_type = asset.charger_type;
          if (asset.arrival_hour) entry.arrival_hour = Number(asset.arrival_hour);
          if (asset.departure_hour) entry.departure_hour = Number(asset.departure_hour);
        }
        if (key === 'cold_storage') {
          if (asset.sqft) entry.sqft = Number(asset.sqft);
          if (asset.setpoint_f) entry.setpoint_f = Number(asset.setpoint_f);
        }
      }
      apiAssets[key] = entry;
    }

    const tariff_selection = tariffType === 'Custom'
      ? {
          type: 'custom',
          energy_rate: Number(customTariff.energy_rate),
          demand_charge: Number(customTariff.demand_charge),
        }
      : { type: 'preset', utility: tariffType };

    return { load_8760_kw, location, assets: apiAssets, tariff_selection };
  };

  const handleBuildTwin = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = buildApiPayload();
      const response = await fetch(`${PHD_API_BASE}/api/calibration/configure`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        let errorDetail;
        try {
          const errData = await response.json();
          errorDetail = errData.error || errData.message || errData.detail;
        } catch (_e) {
          // Response body wasn't JSON
        }
        throw new Error(errorDetail || `HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      onComplete({
        assets: payload.assets,
        tariff_selection: payload.tariff_selection,
        scenario_data: data.scenario_data,
        digital_twin_summary: data.digital_twin_summary,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isActive = (key) => assets[key].relationship !== 'N/A';

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Site Assets
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: vortxColors.stone }}>
        Describe the assets at this site. Select the relationship of each asset to the uploaded load profile.
      </Typography>

      {/* Asset Cards - 2x2 Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
        {/* Solar */}
        <Card sx={cardSx}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <SolarIcon sx={iconSx} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Solar</Typography>
            </Box>
            <ToggleButtonGroup
              value={assets.solar.relationship}
              exclusive
              onChange={(_, val) => val && updateAsset('solar', 'relationship', val)}
              size="small"
              fullWidth
            >
              {RELATIONSHIP_OPTIONS.map(opt => (
                <ToggleButton key={opt} value={opt} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                  {opt}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Collapse in={isActive('solar')}>
              <Box sx={{ mt: 2 }}>
                <TextField
                  label="Capacity (kW)"
                  type="number"
                  size="small"
                  fullWidth
                  value={assets.solar.capacity_kw}
                  onChange={(e) => updateAsset('solar', 'capacity_kw', e.target.value)}
                />
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* BESS */}
        <Card sx={cardSx}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <BatteryIcon sx={iconSx} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>BESS</Typography>
            </Box>
            <ToggleButtonGroup
              value={assets.battery.relationship}
              exclusive
              onChange={(_, val) => val && updateAsset('battery', 'relationship', val)}
              size="small"
              fullWidth
            >
              {RELATIONSHIP_OPTIONS.map(opt => (
                <ToggleButton key={opt} value={opt} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                  {opt}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Collapse in={isActive('battery')}>
              <Box sx={{ mt: 2, display: 'flex', gap: 1.5 }}>
                <TextField
                  label="Capacity (kWh)"
                  type="number"
                  size="small"
                  fullWidth
                  value={assets.battery.capacity_kwh}
                  onChange={(e) => updateAsset('battery', 'capacity_kwh', e.target.value)}
                />
                <TextField
                  label="Power (kW)"
                  type="number"
                  size="small"
                  fullWidth
                  value={assets.battery.power_kw}
                  onChange={(e) => updateAsset('battery', 'power_kw', e.target.value)}
                />
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* EV Fleet */}
        <Card sx={cardSx}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <EvIcon sx={iconSx} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>EV Fleet</Typography>
            </Box>
            <ToggleButtonGroup
              value={assets.ev.relationship}
              exclusive
              onChange={(_, val) => val && updateAsset('ev', 'relationship', val)}
              size="small"
              fullWidth
            >
              {RELATIONSHIP_OPTIONS.map(opt => (
                <ToggleButton key={opt} value={opt} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                  {opt}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Collapse in={isActive('ev')}>
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField
                    label="Vehicles"
                    type="number"
                    size="small"
                    fullWidth
                    value={assets.ev.num_vehicles}
                    onChange={(e) => updateAsset('ev', 'num_vehicles', e.target.value)}
                  />
                  <FormControl size="small" fullWidth>
                    <InputLabel>Charger Type</InputLabel>
                    <Select
                      label="Charger Type"
                      value={assets.ev.charger_type}
                      onChange={(e) => updateAsset('ev', 'charger_type', e.target.value)}
                    >
                      <MenuItem value="L2">L2</MenuItem>
                      <MenuItem value="DCFC">DCFC</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField
                    label="Arrival Hour"
                    type="number"
                    size="small"
                    fullWidth
                    value={assets.ev.arrival_hour}
                    onChange={(e) => updateAsset('ev', 'arrival_hour', e.target.value)}
                    inputProps={{ min: 0, max: 23 }}
                  />
                  <TextField
                    label="Departure Hour"
                    type="number"
                    size="small"
                    fullWidth
                    value={assets.ev.departure_hour}
                    onChange={(e) => updateAsset('ev', 'departure_hour', e.target.value)}
                    inputProps={{ min: 0, max: 23 }}
                  />
                </Box>
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* Cold Storage */}
        <Card sx={cardSx}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <ColdIcon sx={iconSx} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Cold Storage</Typography>
            </Box>
            <ToggleButtonGroup
              value={assets.cold_storage.relationship}
              exclusive
              onChange={(_, val) => val && updateAsset('cold_storage', 'relationship', val)}
              size="small"
              fullWidth
            >
              {RELATIONSHIP_OPTIONS.map(opt => (
                <ToggleButton key={opt} value={opt} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                  {opt}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Collapse in={isActive('cold_storage')}>
              <Box sx={{ mt: 2, display: 'flex', gap: 1.5 }}>
                <TextField
                  label="Square Feet"
                  type="number"
                  size="small"
                  fullWidth
                  value={assets.cold_storage.sqft}
                  onChange={(e) => updateAsset('cold_storage', 'sqft', e.target.value)}
                />
                <TextField
                  label="Setpoint (F)"
                  type="number"
                  size="small"
                  fullWidth
                  value={assets.cold_storage.setpoint_f}
                  onChange={(e) => updateAsset('cold_storage', 'setpoint_f', e.target.value)}
                />
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      </Box>

      {/* Tariff Section */}
      <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>Tariff</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1.5, mb: 3 }}>
        {UTILITIES.map(u => {
          const rate = UTILITY_RATES[u];
          const isSelected = tariffType === u;
          return (
            <Paper
              key={u}
              variant="outlined"
              onClick={() => setTariffType(u)}
              sx={{
                p: 1.5,
                cursor: 'pointer',
                borderColor: isSelected ? vortxColors.gold : vortxColors.stone + '40',
                borderWidth: isSelected ? 2 : 1,
                bgcolor: isSelected ? vortxColors.gold + '10' : 'transparent',
                transition: 'all 0.2s',
                '&:hover': { borderColor: vortxColors.gold },
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {rate.label}
              </Typography>
              {u !== 'Custom' ? (
                <Box>
                  <Typography variant="caption" sx={{ color: vortxColors.stone, display: 'block', lineHeight: 1.6 }}>
                    On-peak: ${rate.onPeak}/kWh
                  </Typography>
                  <Typography variant="caption" sx={{ color: vortxColors.stone, display: 'block', lineHeight: 1.6 }}>
                    Off-peak: ${rate.offPeak}/kWh
                  </Typography>
                  <Typography variant="caption" sx={{ color: vortxColors.stone, display: 'block', lineHeight: 1.6 }}>
                    DC: ${rate.dc}/kW{rate.peakDc > 0 ? ` + $${rate.peakDc}/kW peak` : ''}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                  Enter custom rates
                </Typography>
              )}
            </Paper>
          );
        })}
      </Box>
      <Collapse in={tariffType === 'Custom'}>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
          <TextField
            label="Energy Rate ($/kWh)"
            type="number"
            size="small"
            value={customTariff.energy_rate}
            onChange={(e) => setCustomTariff(prev => ({ ...prev, energy_rate: e.target.value }))}
            sx={{ width: 160 }}
          />
          <TextField
            label="Demand Charge ($/kW)"
            type="number"
            size="small"
            value={customTariff.demand_charge}
            onChange={(e) => setCustomTariff(prev => ({ ...prev, demand_charge: e.target.value }))}
            sx={{ width: 180 }}
          />
        </Box>
      </Collapse>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={onBack}
          disabled={loading}
          sx={{ color: vortxColors.stone, borderColor: vortxColors.stone }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleBuildTwin}
          disabled={loading}
          sx={{
            backgroundColor: vortxColors.gold,
            '&:hover': { backgroundColor: vortxColors.goldHover },
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} sx={{ color: 'inherit' }} />
              Building Digital Twin...
            </Box>
          ) : (
            'Build Digital Twin'
          )}
        </Button>
      </Box>
    </Box>
  );
}
